// @flow
import React, { Fragment } from 'react';
import classnames from 'classnames';
import { isEmpty } from 'lodash';

import { TrackClick } from 'Tracking/track-click/TrackClick.jsx';
import { scrollToElement } from 'Components/utils/scroll/scrollToElement';
import { COLORS } from 'Components/utils/color/colors';
import { ICONS } from 'Components/utils/icon/icons';
import ResponsiveContext from 'Components/utils/responsive/responsiveContext';

import { SiteContainer } from 'Components/frameworks/site-container/SiteContainer.jsx';
import DynamicLink from 'Components/frameworks/dynamic-link/DynamicLink.jsx';
import { Icon, type IconName } from 'Components/frameworks/icon/Icon.jsx';
import { type PostAnswerProps } from 'Components/frameworks/post-answer/PostAnswer.jsx';
import { type PostQuestionProps } from 'Components/frameworks/post-question/PostQuestion.jsx';
import { Text } from 'Components/frameworks/text/Text.jsx';
import { Title } from 'Components/frameworks/title/Title.jsx';
import { WibilongLarge } from './large/WibilongLarge.jsx';
import { WibilongNarrow } from './narrow/WibilongNarrow.jsx';
import {
  WibilongPopinButton,
  type WibilongPopinButtonProps,
} from './popin-button/WibilongPopinButton.jsx';

type Question = {
  ...$Exact<PostQuestionProps>,
  answers: Array<PostAnswerProps>,
  trackingData?: TrackingData,
};

type Link = {|
  label?: string,
  target?: string,
  trackingData?: TrackingData,
  url?: string,
|};

type Props = {
  button: WibilongPopinButtonProps,
  className?: string,
  hasLinkAllQuestions?: boolean,
  icon?: IconName,
  linkAllQuestions?: Link,
  noAnswerstext?: string,
  productId?: string,
  questions: Array<Question>,
  scrollBackwardLabel?: string,
  scrollForwardLabel?: string,
  seeMoreLabel?: string,
  text?: string,
  title?: string,
};

const scrollToQuestions = id => () => {
  scrollToElement(`#${id}`, 300, '.Line-PriceTicket');
};

export function Wibilong(props: Props) {
  const {
    button,
    className,
    hasLinkAllQuestions,
    icon,
    linkAllQuestions = {},
    questions = [],
    text,
    title,
  } = props;

  if (isEmpty(questions)) return null;

  const id = 'wibilong';
  const scrollTo = scrollToQuestions(id);

  return (
    <section
      className={classnames(className, 'bg-white relative shadow-inner-top shadow-inner-bottom')}
      id={id}
    >
      <div className="absolute inset-0" style={{ opacity: '0.05' }}>
        <Icon color={COLORS.TURQUOISE} name={icon} width="40rem" />
      </div>
      <div className="py-9">
        <ResponsiveContext.Consumer>
          {({ isTablet, isDesktop }) => (
            <Fragment>
              {(!!text || !!title) && (
                <SiteContainer>
                  <header className="flex flex-col sm:flex-row items-center justify-end sm:px-16 text-center sm:text-initial">
                    {!!text && (
                      <div className="flex flex-col sm:flex-row items-center sm:rtl:mr-auto w-full">
                        <Icon
                          className="rtl:mr-5"
                          color={COLORS.BLUE}
                          name={ICONS.COMMUNITY}
                          width={isTablet ? '3.2rem' : '4rem'}
                        />
                        <Text className="mt-4 sm:mt-0" theme="text-sans-4" text={text} />
                      </div>
                    )}
                    {!!title && (
                      <Title
                        className="mt-4 sm:mt-0 w-full"
                        theme={isDesktop ? 'title-sans-9' : 'title-sans-4'}
                      >
                        {title}
                      </Title>
                    )}
                  </header>
                </SiteContainer>
              )}
              {isTablet ? (
                <WibilongLarge {...props} scrollTo={scrollTo} />
              ) : (
                <WibilongNarrow {...props} />
              )}
            </Fragment>
          )}
        </ResponsiveContext.Consumer>
        <SiteContainer>
          <footer className="flex flex-col items-center mt-4">
            <WibilongPopinButton className="inline-block" {...button} />
            {hasLinkAllQuestions &&
              linkAllQuestions &&
              linkAllQuestions.url && (
                <TrackClick
                  {...linkAllQuestions}
                  as={DynamicLink}
                  className="font-bold mt-4 mx-8 text-md text-blue focus:text-turquoise hover:text-turquoise underline transition-color"
                >
                  {linkAllQuestions.label}
                </TrackClick>
              )}
          </footer>
        </SiteContainer>
      </div>
    </section>
  );
}
